import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { get, post } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import AddArtworkDialog from '@/components/AddArtworkDialog';
import { removeDesignFromTemplate, type Template } from '@/lib/design-recommend';
import { lineupCoverage, sortArtworkTemplates, sortProductTemplates } from '@/lib/store-template-kinds';
import { productLineupHref, templateEditorHref } from '@/lib/template-editor-query';

// Store Builder — the two-axis store composer.
//   Products axis  = a product template (e.g. "general") → which garments are in the store.
//   Artwork axis   = a sport template (e.g. "Volleyball") → which design goes on each garment.
// Edit the artwork via "Add artwork" (recommends products) and the lineup-strip × (remove).

type Product = {
  id: string; style: string; position: string; color: string;
  category: string; displayName: string; image: string;
  box: { x: number; y: number; w: number; h: number };
};
type Recipe = { id: string; name?: string; sport?: string; category?: string; defaultPrintArea?: string };

const SAMPLE_LOGO = 'https://storage.googleapis.com/ghostfit-artwork-files/wizard-uploads/1777316233112-Ashwaubenon_Baseball.png';
const SAMPLE_COLORWAY = ['navy', 'gold', 'white'];
// product-config position codes → the kebab names sport templates store
const POS: Record<string, string> = { fc: 'full-chest', lc: 'left-chest', ll: 'left-leg', ch: 'center-hat', fb: 'full-back' };
const designCache = new Map<string, string>(); // `${designId}|${garmentColor}` → object URL

// Render a design, contrast-adapted for the garment color it will sit on.
async function renderDesign(designId: string, sport?: string, garmentColor?: string): Promise<string> {
  const key = `${designId}|${garmentColor || ''}`;
  const cached = designCache.get(key);
  if (cached) return cached;
  const res = await fetch(`/api/recipes/${encodeURIComponent(designId)}/preview`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({
      schoolData: {
        colorway: SAMPLE_COLORWAY,
        team_text: 'CAMELOT', mascot_text: 'DRAGONS', year: '2026',
        sport: (sport || 'SPIRIT').replace(/_/g, ' ').toUpperCase(),
      },
      logoUrl: SAMPLE_LOGO,
      garmentColor: garmentColor || undefined,
      format: 'png',
      outputSize: { width: 600, height: 600 },
    }),
  });
  if (!res.ok) throw new Error(`render ${res.status}`);
  const url = URL.createObjectURL(await res.blob());
  designCache.set(key, url);
  return url;
}

function ProductCard({ p, designUrl, label }: { p: Product; designUrl: string; label?: string }) {
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const pct = nat
    ? { left: (p.box.x / nat.w) * 100, top: (p.box.y / nat.h) * 100, width: (p.box.w / nat.w) * 100, height: (p.box.h / nat.h) * 100 }
    : null;
  return (
    <div className="rounded-md border border-border bg-white p-2 shadow-card">
      <div className="relative w-full overflow-hidden rounded-sm bg-bg" style={{ aspectRatio: nat ? `${nat.w} / ${nat.h}` : '1 / 1' }}>
        <img
          src={p.image}
          alt={p.displayName}
          className="block w-full"
          onLoad={(e) => setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
        />
        {pct && designUrl && (
          <div className="absolute" style={{ left: `${pct.left}%`, top: `${pct.top}%`, width: `${pct.width}%`, height: `${pct.height}%` }}>
            <img src={designUrl} alt="" className="h-full w-full object-contain" />
          </div>
        )}
      </div>
      <div className="mt-1 flex items-center justify-between gap-1">
        <span className="truncate text-[10.5px] text-text/80">{p.displayName}</span>
        <span className="shrink-0 rounded-sm bg-bg px-1 text-[9px] uppercase text-subtle">{p.position}</span>
      </div>
      {label && <div className="mt-0.5 truncate text-[9px] text-primary/80">{label}</div>}
    </div>
  );
}

export default function StoreBuilder() {
  const { data: tplData, mutate: mutateTpls } = useSWR<{ templates?: Template[] }>('/api/store-templates', get);
  const { data: recipeData } = useSWR<{ recipes?: Recipe[] }>('/api/recipes', get);
  const templates: Template[] = tplData?.templates || [];
  const recipes: Recipe[] = recipeData?.recipes || [];
  const productTemplates = useMemo(() => sortProductTemplates(templates), [templates]);
  const artworkTemplates = useMemo(() => sortArtworkTemplates(templates), [templates]);

  const [productTplId, setProductTplId] = useState('general');
  const [artworkTplId, setArtworkTplId] = useState(''); // '' = logo only
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  const productTpl = useMemo(() => productTemplates.find((t) => t.id === productTplId) || null, [productTemplates, productTplId]);
  const artworkTpl = artworkTemplates.find((t) => t.id === artworkTplId) || null;
  const coverage = lineupCoverage(productTpl?.styles || [], artworkTpl?.styles || []);

  async function saveTemplate(updated: Template) {
    setSaving(true);
    setStatus('saving…');
    try {
      await post('/api/store-templates', updated);
      await mutateTpls();
      setStatus('✓ saved');
      setAddOpen(false);
    } catch (e) {
      const err = e as { status?: number; body?: { error?: string; details?: string[] } };
      setStatus(`save failed${err.status ? ` (${err.status})` : ''}: ${err.body?.error || (e as Error).message}${err.body?.details?.length ? ' — ' + err.body.details.join('; ') : ''}`);
    } finally {
      setSaving(false);
    }
  }

  async function removeDesign(designId: string, label: string) {
    if (!artworkTpl) return;
    if (!confirm(`Remove "${label}" from the ${artworkTpl.name} template? This removes all its placements. (Affects future stores of this sport.)`)) return;
    await saveTemplate(removeDesignFromTemplate(artworkTpl, designId));
  }

  const { data: prodData, isLoading } = useSWR<{ products?: Product[] }>(
    `/api/store-preview-products?template=${encodeURIComponent(productTplId)}`, get,
  );
  const products: Product[] = prodData?.products || [];

  // (styleNumber|kebabPosition) → { designTemplateId, label }
  const designByStyle = useMemo(() => {
    const m: Record<string, { id: string; label?: string }> = {};
    artworkTpl?.styles?.forEach((s) =>
      s.designs?.forEach((d) => { m[`${String(s.styleNumber).toUpperCase()}|${d.position}`] = { id: d.designTemplateId, label: d.label }; }),
    );
    return m;
  }, [artworkTpl]);

  function designForProduct(p: Product): { id?: string; label?: string } {
    if (!artworkTpl) return {};
    return designByStyle[`${String(p.style).toUpperCase()}|${POS[p.position] || p.position}`] || {};
  }

  // distinct designs in the chosen artwork template → render each once
  const distinctDesigns = useMemo(() => {
    const seen = new Map<string, string>(); // id → label
    artworkTpl?.styles?.forEach((s) => s.designs?.forEach((d) => { if (!seen.has(d.designTemplateId)) seen.set(d.designTemplateId, d.label || ''); }));
    return Array.from(seen.entries()).map(([id, label]) => ({ id, label }));
  }, [artworkTpl]);

  // (designId × garment-color) pairs actually used across the product set — each
  // rendered once, contrast-adapted to its garment color.
  const designJobs = useMemo(() => {
    if (!artworkTpl) return [] as { id: string; color: string }[];
    const seen = new Set<string>(); const jobs: { id: string; color: string }[] = [];
    for (const p of products) {
      const id = designForProduct(p).id;
      if (!id) continue;
      const k = `${id}|${p.color}`;
      if (seen.has(k)) continue; seen.add(k);
      jobs.push({ id, color: p.color });
    }
    return jobs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artworkTpl, products, designByStyle]);

  const [designUrls, setDesignUrls] = useState<Record<string, string>>({}); // `${id}|${color}` → url
  const [rendering, setRendering] = useState(false);
  useEffect(() => {
    if (!artworkTpl || !designJobs.length) { setDesignUrls({}); return; }
    let alive = true;
    setRendering(true);
    Promise.all(
      designJobs.map((j) => renderDesign(j.id, artworkTpl.sport, j.color)
        .then((u) => [`${j.id}|${j.color}`, u] as const).catch(() => [`${j.id}|${j.color}`, ''] as const)),
    ).then((pairs) => { if (alive) { setDesignUrls(Object.fromEntries(pairs)); setRendering(false); } });
    return () => { alive = false; };
  }, [artworkTpl, designJobs]);

  function urlForProduct(p: Product): { url: string; label?: string } {
    if (!artworkTpl) return { url: SAMPLE_LOGO, label: 'Logo' };
    const { id, label } = designForProduct(p);
    return { url: id ? designUrls[`${id}|${p.color}`] || '' : '', label };
  }

  // Lineup strip: any rendered variant of each distinct design.
  function lineupUrl(id: string): string {
    const hit = Object.keys(designUrls).find((k) => k.startsWith(`${id}|`));
    return hit ? designUrls[hit] : '';
  }

  const byCategory = useMemo(() => {
    const order = ['hoodies', 'crewnecks', 'tees', 'quarter-zips', 'polos', 'joggers', 'hats', 'bags'];
    const m: Record<string, Product[]> = {};
    for (const p of products) (m[p.category] = m[p.category] || []).push(p);
    return Object.entries(m).sort((a, b) => {
      const ia = order.indexOf(a[0]); const ib = order.indexOf(b[0]);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
  }, [products]);

  return (
    <div className="px-7 py-6">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tighter">Store Builder</h1>
          <p className="mt-0.5 text-[12px] text-muted">
            Pick a <strong>product template</strong> (the garments) and a <strong>sport artwork</strong> (the designs). Each product shows its
            mapped design at the approved placement. Previewed on a sample school (CAMELOT / DRAGONS, navy/gold).
          </p>
        </div>
        {/* "Push to live stores / Schedule a drop" belongs here, but the fleet
            /rollout and /drops endpoints ship with the unmerged #355 — a link
            to a page that cannot push anything is worse than no link. */}
      </div>

      {/* two-axis selector */}
      <div className="mb-5 flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium text-subtle">Products</span>
          <select value={productTplId} onChange={(e) => setProductTplId(e.target.value)}
            className="h-9 min-w-[200px] rounded-sm border border-border bg-white px-2 text-[13px]">
            {productTemplates.map((t) => <option key={t.id} value={t.id}>{t.name || t.id}</option>)}
          </select>
        </label>
        <span className="pb-2 text-[16px] text-subtle">×</span>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium text-subtle">Sport artwork</span>
          <select value={artworkTplId} onChange={(e) => setArtworkTplId(e.target.value)}
            className="h-9 min-w-[220px] rounded-sm border border-border bg-white px-2 text-[13px] capitalize">
            <option value="">Logo only (no designs)</option>
            {artworkTemplates.map((t) => <option key={t.id} value={t.id}>{t.name || t.id}</option>)}
          </select>
        </label>
        <Button
          variant="secondary"
          disabled={!artworkTpl}
          onClick={() => setAddOpen(true)}
          className="mb-[2px]"
          title={artworkTpl ? 'Add artwork to this sport template' : 'Pick a sport artwork template first'}
        >
          + Add artwork
        </Button>
        <Link to={productLineupHref({ templateId: productTplId })} className="mb-[6px] text-[11px] text-primary underline">
          Edit garments →
        </Link>
        {artworkTpl && (
          <Link to={templateEditorHref({ templateId: artworkTplId })} className="mb-[6px] text-[11px] text-primary underline">
            Assign designs →
          </Link>
        )}
        {artworkTpl && (
          <div className="pb-1 text-[11px] text-muted">
            {distinctDesigns.length} designs · covers {coverage.covered} of {coverage.total} lineup garments{rendering ? ' · rendering…' : ''}
          </div>
        )}
        {status && <div className="pb-1 text-[11px] text-muted">{status}</div>}
      </div>

      {/* design lineup strip */}
      {artworkTpl && (
        <div className="mb-5 rounded-md bg-surface p-3 shadow-card">
          <div className="mb-2 text-[11px] font-semibold text-text/80">{artworkTpl.name} — design lineup</div>
          <div className="flex flex-wrap gap-3">
            {distinctDesigns.map((d) => (
              <div key={d.id} className="w-[96px]">
                <div className="aspect-square w-full overflow-hidden rounded-sm border border-border bg-white"
                  style={{ background: 'repeating-conic-gradient(#f1f1f3 0% 25%, #ffffff 0% 50%) 0 / 12px 12px' }}>
                  {lineupUrl(d.id) ? <img src={lineupUrl(d.id)} alt="" className="h-full w-full object-contain" /> : <Skeleton className="h-full w-full" />}
                </div>
                <div className="mt-1 flex items-center gap-1">
                  <div className="flex-1 truncate text-[9px] text-subtle" title={d.label}>{d.label || d.id}</div>
                  <button
                    onClick={() => removeDesign(d.id, d.label || d.id)}
                    disabled={saving}
                    title="Remove this artwork from the template"
                    className="h-4 w-4 shrink-0 rounded-sm border border-border text-[10px] leading-none text-muted hover:bg-bg disabled:opacity-50"
                  >×</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* store preview */}
      {isLoading && <div className="text-[12px] text-muted">Loading products…</div>}
      {!isLoading && !products.length && (
        <div className="rounded-md border border-dashed border-border p-10 text-center text-[12px] text-muted">No products for this template.</div>
      )}
      {byCategory.map(([cat, items]) => (
        <section key={cat} className="mb-5">
          <h3 className="mb-2 text-[12px] font-semibold capitalize text-text/80">{cat} <span className="text-subtle">· {items.length}</span></h3>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
            {items.map((p) => { const { url, label } = urlForProduct(p); return <ProductCard key={p.id} p={p} designUrl={url} label={label} />; })}
          </div>
        </section>
      ))}

      <AddArtworkDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        template={artworkTpl}
        productTemplate={productTpl}
        products={products}
        recipes={recipes}
        saving={saving}
        onApply={saveTemplate}
      />
    </div>
  );
}
